import { showSuccess, showErrorFromMutation } from "@/shared/utils/toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUsersList, reviewTrialRequest } from "@/shared/api";

const USERS_LIST_QUERY_KEY = "users-list";

/**
 * Fetch users list (auto-refreshes every 15 s so admin dashboards stay current).
 *
 * `refetchInterval` is a parameter because not every caller needs 15 s: the sidebar
 * badge mounts on every screen for admins and only needs a count, so polling a
 * 100-row list that fast was the most frequent request in the whole app.
 */
export const useFetchUsersList = (options = {}) => {
  const {
    limit = 50,
    offset = 0,
    enabled = true,
    refetchInterval = 15_000,
    refetchOnWindowFocus = true,
    staleTime = 5_000,
  } = options;
  return useQuery({
    queryKey: [USERS_LIST_QUERY_KEY, limit, offset],
    queryFn: () => fetchUsersList({ limit, offset }),
    enabled,
    refetchInterval,
    refetchOnWindowFocus,
    staleTime,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to fetch users list. Please try again.",
      );
    },
  });
};

/** Approve or reject trial request */
export const useReviewTrialRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, action }) => reviewTrialRequest(userId, action),
    onSuccess: (_, variables) => {
      const msg =
        variables.action === "approve"
          ? "Request approved successfully!"
          : "Request rejected successfully.";
      showSuccess(msg);
      queryClient.invalidateQueries({ queryKey: [USERS_LIST_QUERY_KEY] });
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message || "Failed to process request. Please try again.",
      );
    },
  });
};
